"use client"

import { useRef, useCallback, useMemo, useEffect, useState } from "react"
import Image from "next/image"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useTranslations } from "next-intl"
import { Icon } from "@/shared/components/icons"
import { LocalizedLink } from "@/shared/components/i18n/localized-link"
import { motion } from "framer-motion"
import type { GenerationRow } from "@/shared/lib/supabase/types"
import { useUIStore } from "@/shared/stores/ui-store"
import {
  useGenerationStore,
  useGenerations,
  useGenerateImage,
  GENERATION_COST_PER_IMAGE,
  pickRandomSessionAspectRatio,
} from "@/features/generation"
import { hasStoredGenerationSession } from "@/features/generation/store"
import { publishToGallery } from "@/features/gallery/actions"
import { useCoins } from "@/features/credits/hooks/use-credits"
import { cn } from "@/shared/lib/utils"
import { queryKeys } from "@/shared/lib/query-keys"
import { PromptBar } from "@/features/generation/components/prompt-bar"
import { GenerationFeed } from "@/features/generation/components/generation-feed"
import { HistorySidebar } from "@/features/generation/components/history-sidebar"
import { track } from "@/shared/lib/analytics"
import { captureClientException } from "@/shared/lib/monitoring/capture-client"
import { isTosViolationMessage } from "@/features/generation/lib/tos-violation"
import { generationFailureKindProps } from "@/features/generation/lib/stream-request-generation"
import { toastTosViolation } from "@/features/generation/lib/toast-tos-violation"

/** Walk up from an element to find the nearest scrollable ancestor. */
function getScrollParent(el: HTMLElement): HTMLElement | null {
  let parent = el.parentElement
  while (parent) {
    if (parent.scrollHeight > parent.clientHeight) {
      const style = getComputedStyle(parent)
      if (style.overflowY === "auto" || style.overflowY === "scroll") {
        return parent
      }
    }
    parent = parent.parentElement
  }
  return null
}

interface HeroPreviewPost {
  id: string
  mediaUrl: string
  thumbnailUrl?: string
  captionKey?: "explicitPortrait" | "privateMuse" | "adultFantasy" | "afterDark"
  objectPosition?: string
}

const HERO_PREVIEW_POSTS: HeroPreviewPost[] = [
  {
    id: "civitai-preview-65105389",
    mediaUrl: "/images/generate/previews/civitai-preview-1.jpeg",
    captionKey: "explicitPortrait",
    objectPosition: "50% 36%",
  },
  {
    id: "civitai-preview-9232495",
    mediaUrl: "/images/generate/previews/civitai-preview-2.jpeg",
    captionKey: "privateMuse",
    objectPosition: "50% 32%",
  },
  {
    id: "civitai-preview-29213825",
    mediaUrl: "/images/generate/previews/civitai-preview-3.jpeg",
    captionKey: "adultFantasy",
    objectPosition: "50% 35%",
  },
  {
    id: "civitai-preview-10339681",
    mediaUrl: "/images/generate/previews/civitai-preview-4.jpeg",
    captionKey: "afterDark",
    objectPosition: "50% 42%",
  },
]

type PreviewCaptions = Record<NonNullable<HeroPreviewPost["captionKey"]>, string>

function previewTitle(
  post: HeroPreviewPost,
  index: number,
  captions: PreviewCaptions
): string {
  const fallback = [
    captions.explicitPortrait,
    captions.adultFantasy,
    captions.privateMuse,
    captions.afterDark,
  ][index]
  const title = (post.captionKey ? captions[post.captionKey] : "").trim() || fallback
  return title.length > 24 ? `${title.slice(0, 21)}...` : title
}

function HeroPreviewCardImage({
  thumbnailUrl,
  mediaUrl,
  objectPosition,
  priority,
  alt,
}: {
  thumbnailUrl?: string
  mediaUrl: string
  objectPosition?: string
  priority?: boolean
  alt: string
}) {
  const candidates = useMemo(
    () => [thumbnailUrl, mediaUrl].filter((url): url is string => Boolean(url?.trim())),
    [thumbnailUrl, mediaUrl]
  )
  const [candidateIndex, setCandidateIndex] = useState(0)
  const imageUrl = candidates[candidateIndex]

  useEffect(() => {
    setCandidateIndex(0)
  }, [thumbnailUrl, mediaUrl])

  if (!imageUrl) return null

  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={imageUrl}
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover"
        style={{ objectPosition }}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => {
          setCandidateIndex((current) =>
            current + 1 < candidates.length ? current + 1 : current
          )
        }}
      />
    )
  }

  return (
    <Image
      src={imageUrl}
      alt={alt}
      fill
      priority={priority}
      sizes="(max-width: 640px) 116px, 142px"
      className="object-cover"
      style={{ objectPosition }}
    />
  )
}

function GenerateHeroPreviewStack({
  posts,
  labels,
}: {
  posts: HeroPreviewPost[]
  labels: {
    featuredPreviews: string
    previewUnavailable: string
    previewAlt: string
    previewCaptions: PreviewCaptions
  }
}) {
  const cardClassNames = [
    "-rotate-5 translate-y-6 sm:-translate-x-3 sm:translate-y-7",
    "rotate-0 z-20 -ml-10 sm:-ml-7 translate-y-10 sm:translate-y-11",
    "rotate-3 z-30 -ml-9 sm:-ml-8 translate-y-4 sm:translate-y-5",
    "-rotate-2 z-10 -ml-9 sm:-ml-7 translate-y-10 sm:translate-y-11",
  ]
  const visibleCardClassNames = cardClassNames

  return (
    <LocalizedLink
      href="/explore"
      className="relative mx-auto flex h-[220px] w-full max-w-[620px] cursor-pointer items-center justify-center overflow-visible focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-4 focus-visible:ring-offset-background sm:h-[260px]"
      aria-label={labels.featuredPreviews}
    >
      {visibleCardClassNames.map((cardClassName, index) => {
        const post = posts[index]

        return (
          <motion.div
            key={post?.id ?? `preview-placeholder-${index}`}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.5,
              delay: index * 0.06,
              ease: [0.22, 1, 0.36, 1] as const,
            }}
            className={cn(
              "relative h-[158px] w-[116px] shrink-0 overflow-hidden rounded-xl border border-white/[0.08] bg-white/[0.04]",
              "sm:h-[190px] sm:w-[142px]",
              cardClassName
            )}
          >
            {post ? (
              <HeroPreviewCardImage
                thumbnailUrl={post.thumbnailUrl}
                mediaUrl={post.mediaUrl}
                objectPosition={post.objectPosition}
                priority={index === 1}
                alt={labels.previewAlt}
              />
            ) : (
              <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.08),rgba(255,255,255,0.02))]" />
            )}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent px-3 pb-3 pt-10">
              <p className="truncate text-[11px] font-semibold text-white sm:text-sm">
                {post
                  ? previewTitle(post, index, labels.previewCaptions)
                  : labels.previewUnavailable}
              </p>
            </div>
          </motion.div>
        )
      })}
    </LocalizedLink>
  )
}

export function GenerateImageContent() {
  const t = useTranslations("generate")
  const feedRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  // Synchronous submit lock. `generateMutation.isPending` only flips after
  // mutate() runs, so it can't guard the async gap before it (the coin check);
  // this ref closes that window so rapid repeat clicks can't fire duplicate jobs.
  const submitLockRef = useRef(false)
  const queryClient = useQueryClient()
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed)
  const setPaywallContext = useUIStore((s) => s.setPaywallContext)

  const scrollToBottom = useCallback((delay = 150, behavior: ScrollBehavior = "smooth") => {
    setTimeout(() => {
      const el = bottomRef.current
      if (!el) return
      const scrollParent = getScrollParent(el)
      if (scrollParent) {
        scrollParent.scrollTo({ top: scrollParent.scrollHeight, behavior })
      }
    }, delay)
  }, [])

  // Store
  const prompt = useGenerationStore((s) => s.prompt)
  const style = useGenerationStore((s) => s.style)
  const aspectRatio = useGenerationStore((s) => s.aspectRatio)
  const numImages = useGenerationStore((s) => s.numImages)
  const negativePrompt = useGenerationStore((s) => s.negativePrompt)
  const guidanceScale = useGenerationStore((s) => s.guidanceScale)
  const promptModifiers = useGenerationStore((s) => s.promptModifiers)
  const setPrompt = useGenerationStore((s) => s.setPrompt)
  const resetSettings = useGenerationStore((s) => s.resetSettings)
  const historyCollapsed = useGenerationStore((s) => s.historyCollapsed)
  const toggleHistory = useGenerationStore((s) => s.toggleHistory)
  const activeSessionIds = useGenerationStore((s) => s.activeSessionIds)
  const setActiveSession = useGenerationStore((s) => s.setActiveSession)
  const [tosViolation, setTosViolation] = useState<string | null>(null)
  const [hasHydratedSession, setHasHydratedSession] = useState(false)

  // Rehydrate persisted Zustand store on mount to avoid SSR/client mismatch.
  // New browser tab/session (no sessionStorage): pick 1:1 or 2:3 at random once.
  useEffect(() => {
    const storageKey = "pleasurai-generation"
    const hadStorage = hasStoredGenerationSession(storageKey)

    void Promise.resolve(useGenerationStore.persist.rehydrate())
      .then(() => {
        if (!hadStorage) {
          useGenerationStore.getState().setAspectRatio(pickRandomSessionAspectRatio())
        }
      })
      .finally(() => setHasHydratedSession(true))
  }, [])

  // Credits
  const { canUse, refetchBalance } = useCoins()

  // Data
  const { data: generations = [], isLoading: loadingHistory } = useGenerations()
  const generateMutation = useGenerateImage()

  const displayedGenerations = useMemo(() => {
    if (activeSessionIds === null) return []
    return generations.filter((g) => activeSessionIds.includes(g.id))
  }, [generations, activeSessionIds])

  const hasGenerations = displayedGenerations.length > 0 || generateMutation.isPending

  // Scroll to bottom when generation starts, session changes, or history loads
  const prevIsPendingRef = useRef(false)
  const prevSessionRef = useRef(activeSessionIds)
  const prevLoadingRef = useRef(true)

  useEffect(() => {
    if (generateMutation.isPending && !prevIsPendingRef.current) {
      scrollToBottom(150, "smooth")
    }
    prevIsPendingRef.current = generateMutation.isPending
  }, [generateMutation.isPending, scrollToBottom])

  useEffect(() => {
    if (activeSessionIds !== prevSessionRef.current && activeSessionIds !== null) {
      scrollToBottom(100, "instant")
    }
    prevSessionRef.current = activeSessionIds
  }, [activeSessionIds, scrollToBottom])

  useEffect(() => {
    if (prevLoadingRef.current && !loadingHistory && displayedGenerations.length > 0) {
      scrollToBottom(100, "instant")
    }
    prevLoadingRef.current = loadingHistory
  }, [loadingHistory, displayedGenerations.length, scrollToBottom])

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return
    // Guard against repeat clicks firing concurrent duplicate generations while
    // one is already in flight (each one deducts coins and starts a new job).
    // Acquire the lock synchronously so it also covers the async coin check below.
    if (submitLockRef.current || generateMutation.isPending) return
    submitLockRef.current = true

    const totalCost = numImages * GENERATION_COST_PER_IMAGE
    setPaywallContext({
      intent: "image_generation",
      trigger: "image_generation_limit",
      characterId: null,
      characterName: null,
      characterAvatar: null,
      messagesSentCount: null,
    })

    // Release the lock on every path that does not hand off to mutate():
    // a rejected coin-check promise or a denied check would otherwise leave
    // the page locked until refresh. After mutate(), onSettled owns release.
    let check: Awaited<ReturnType<typeof canUse>>
    try {
      check = await canUse("image_generation", totalCost)
    } catch (err) {
      // The coin check itself failed (e.g. network). PromptBar fires this
      // callback without awaiting, so rethrowing would surface nowhere —
      // handle it here: give the user feedback and record the failure.
      submitLockRef.current = false
      const reason = err instanceof Error ? err.message : String(err)
      track("gen:image_fail", { style, reason })
      captureClientException(err, {
        scope: "generation",
        extra: { style, source: "coin_check_error" },
      })
      toast.error(t("failed"))
      return
    }
    if (!check.allowed) {
      submitLockRef.current = false
      return
    }

    setTosViolation(null)

    track("gen:image_request", {
      style,
      aspect_ratio: aspectRatio,
      has_negative_prompt: !!negativePrompt.trim(),
      prompt_modifier_count: Object.keys(promptModifiers).length,
    })

    generateMutation.mutate(
      {
        prompt: prompt.trim(),
        style,
        aspectRatio,
        numImages,
        negativePrompt: negativePrompt.trim() || undefined,
        guidanceScale,
        promptModifiers,
      },
      {
        onSuccess: (result) => {
          if (result.error != null) {
            const isTos =
              result.tosViolation === true || isTosViolationMessage(result.error)
            if (isTos) {
              const msg =
                String(result.error).trim() ||
                t("tosViolation")
              track("gen:image_blocked", {
                source: result.tosSource ?? "unknown",
                category: result.tosCategory ?? "unspecified",
                style,
                aspect_ratio: aspectRatio,
              })
              setTosViolation(msg)
              toastTosViolation(msg)
            } else {
              const reason = String(result.error)
              // Close the observability hole: a failed paid flow must leave a
              // trail. Always emit the analytics event; capture an exception with
              // the HTTP status so vendor throttling (429) / billing (402) is
              // visible — unless the stream layer already captured the underlying
              // transport error, to avoid duplicate exception records.
              track("gen:image_fail", { style, reason, status: result.status })
              if (!result.captured) {
                captureClientException(new Error(reason), {
                  scope: "generation",
                  extra: {
                    style,
                    status: result.status,
                    source: "result_error",
                    ...generationFailureKindProps(result.failureKind, "result_error"),
                  },
                })
              }
              toast.error(reason)
            }
            return
          }
          if (result.generation) {
            queryClient.setQueryData<GenerationRow[]>(
              queryKeys.generations.all(),
              (old) => (old ? [result.generation!, ...old] : [result.generation!])
            )

            track("gen:image_complete", {
              style,
              generation_id: result.generation.id,
            })

            setPrompt("")
            const current = useGenerationStore.getState().activeSessionIds
            setActiveSession([...(current ?? []), result.generation.id])
            toast.success(t("imagesGenerated"))
            scrollToBottom(200)
            refetchBalance()

            // Auto-publish to Explore (fire-and-forget)
            const gen = result.generation!
            const shouldPublish = useGenerationStore.getState().publishToExplore
            if (shouldPublish && gen.status === "completed") {
              const params = gen.parameters as Record<string, unknown>
              const outputUrls = (params?.output_urls as string[]) ?? (gen.output_url ? [gen.output_url] : [])
              const genStyle = (params?.style as string) ?? style
              const genAspect = (params?.aspectRatio as string) ?? aspectRatio

              track("gen:publish_to_gallery", {
                generation_id: gen.id,
                style: genStyle,
              })

              for (const url of outputUrls) {
                publishToGallery({
                  imageUrl: url,
                  prompt: gen.prompt,
                  style: genStyle,
                  aspectRatio: genAspect,
                  generationId: gen.id,
                  negativePrompt: (params?.negativePrompt as string) ?? undefined,
                  guidanceScale: (params?.guidanceScale as number) ?? undefined,
                }).catch(() => {
                  // Silent fail — auto-publish is best-effort
                })
              }
            }
          }
        },
        onError: (err) => {
          const msg = err instanceof Error ? err.message : String(err)
          if (isTosViolationMessage(msg)) {
            track("gen:image_blocked", {
              source: "unknown",
              category: "unspecified",
              style,
              aspect_ratio: aspectRatio,
            })
            setTosViolation(msg)
            toastTosViolation(msg)
          } else {
            track("gen:image_fail", { style, reason: msg })
            captureClientException(err, {
              scope: "generation",
              extra: { style, source: "mutation_error" },
            })
            toast.error(t("failed"))
          }
        },
        onSettled: () => {
          submitLockRef.current = false
        },
      }
    )
  }, [prompt, style, aspectRatio, numImages, negativePrompt, guidanceScale, promptModifiers, generateMutation, setPrompt, canUse, refetchBalance, setActiveSession, queryClient, scrollToBottom, setPaywallContext, t])

  const handleSessionSelect = useCallback(
    (sessionIds: string[]) => {
      setActiveSession(sessionIds)
      if (!historyCollapsed) toggleHistory() // Close history sidebar immediately on select
      // Only switch which images are displayed — do NOT replace the prompt bar
    },
    [setActiveSession, historyCollapsed, toggleHistory]
  )

  const handleNew = useCallback(() => {
    resetSettings()
  }, [resetSettings])

  if (!hasHydratedSession) {
    return (
      <>
        <h1 className="sr-only">{t("title")}</h1>
        <div
          className="min-h-[calc(100dvh-9rem)] flex-1 bg-background"
          aria-busy="true"
          aria-label={t("loadingPreview")}
        />
      </>
    )
  }

  return (
    <div
      className={cn(
        "relative flex flex-1 min-h-0 min-w-0",
        historyCollapsed ? "lg:pr-[68px]" : "lg:pr-[276px]"
      )}
    >
      {/* Main content area */}
      {!hasGenerations ? (
        <div className="relative flex flex-1 min-w-0 flex-col overflow-y-auto overscroll-contain px-3 pb-5 pt-6 sm:px-4">
          <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden />

          <div className="flex min-h-[calc(100dvh-9rem)] flex-1 flex-col items-center justify-between gap-8">
            <div className="w-full" aria-hidden />

            <section className="relative flex w-full flex-col items-center">
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] as const }}
                className="relative z-40 mb-2 flex items-center justify-center gap-3"
              >
                <span className="flex size-9 items-center justify-center rounded-xl border border-white/[0.14] bg-white text-accent-blue sm:size-10">
                  <Icon name="image" variant="bold" size={22} />
                </span>
                <h1 className="text-4xl font-semibold leading-none text-white sm:text-5xl">
                  {t("title")}
                </h1>
              </motion.div>

              <GenerateHeroPreviewStack
                posts={HERO_PREVIEW_POSTS}
                labels={{
                  featuredPreviews: t("featuredPreviews"),
                  previewUnavailable: t("previewUnavailable"),
                  previewAlt: t("previewAlt"),
                  previewCaptions: {
                    explicitPortrait: t("previewCaptions.explicitPortrait"),
                    privateMuse: t("previewCaptions.privateMuse"),
                    adultFantasy: t("previewCaptions.adultFantasy"),
                    afterDark: t("previewCaptions.afterDark"),
                  },
                }}
              />
            </section>

            <motion.div
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.12, ease: [0.22, 1, 0.36, 1] as const }}
              className="relative w-full max-w-3xl pb-1"
            >
              <PromptBar
                onGenerate={handleGenerate}
                isPending={generateMutation.isPending}
                tosViolation={tosViolation}
                onDismissTos={() => setTosViolation(null)}
              />
            </motion.div>
          </div>
        </div>
      ) : (
        /* Feed state — scroll inside bounded container, prompt bar fixed at bottom */
        <div className="relative flex-1 min-h-0 flex flex-col min-w-0">
          <h1 className="sr-only">{t("title")}</h1>
          {/* Scrollable feed area */}
          <div
            ref={feedRef}
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 pb-44"
          >
            <GenerationFeed
              generations={displayedGenerations}
              pendingGeneration={
                generateMutation.isPending
                  ? { prompt, style, aspectRatio, numImages }
                  : null
              }
              generationProgress={generateMutation.generationProgress}
              isPending={generateMutation.isPending}
            />
            <div ref={bottomRef} />
          </div>

          {/* Prompt bar — viewport-fixed; z-50 above feed + sticky topbar (z-30) */}
          <div
            className={cn(
              "fixed bottom-0 left-0 right-0 z-[100] pointer-events-none",
              sidebarCollapsed ? "lg:left-[72px]" : "lg:left-[260px]"
            )}
            style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom, 0px))" }}
          >
            <div className="pointer-events-auto bg-transparent pt-5 pb-3 px-4">
              <PromptBar
                onGenerate={handleGenerate}
                isPending={generateMutation.isPending}
                tosViolation={tosViolation}
                onDismissTos={() => setTosViolation(null)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Right sidebar — desktop only, generation history */}
      <HistorySidebar
        generations={generations}
        isLoading={loadingHistory}
        activeSessionIds={activeSessionIds}
        collapsed={historyCollapsed}
        onSelect={handleSessionSelect}
        onNew={handleNew}
        onToggleCollapse={toggleHistory}
      />

    </div>
  )
}
